"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { featureModuleRows } from "./featureModulesData";
import { useHomeLanguage } from "./HomeLanguageContext";

const categoryStyle: Record<string, string> = {
  HRM: "bg-emerald-50 text-emerald-700 border-emerald-100",
  Accounting: "bg-teal-50 text-teal-700 border-teal-100",
  CRM: "bg-lime-50 text-lime-700 border-lime-100",
  Project: "bg-green-50 text-green-700 border-green-100",
  Payroll: "bg-emerald-50 text-emerald-700 border-emerald-100",
  Tools: "bg-teal-50 text-teal-700 border-teal-100",
  AI: "bg-lime-50 text-lime-700 border-lime-100",
  Recruitment: "bg-green-50 text-green-700 border-green-100",
  Device: "bg-emerald-50 text-emerald-700 border-emerald-100",
  Security: "bg-teal-50 text-teal-700 border-teal-100",
  Support: "bg-lime-50 text-lime-700 border-lime-100",
  Asset: "bg-green-50 text-green-700 border-green-100",
  Collaboration: "bg-emerald-50 text-emerald-700 border-emerald-100",
  POS: "bg-amber-50 text-amber-700 border-amber-100",
  Inventory: "bg-orange-50 text-orange-700 border-orange-100",
  Procurement: "bg-cyan-50 text-cyan-700 border-cyan-100",
  Logistics: "bg-sky-50 text-sky-700 border-sky-100",
  Finance: "bg-indigo-50 text-indigo-700 border-indigo-100",
  LMS: "bg-violet-50 text-violet-700 border-violet-100",
  Analytics: "bg-blue-50 text-blue-700 border-blue-100",
  Integration: "bg-slate-50 text-slate-700 border-slate-200",
  Mobile: "bg-rose-50 text-rose-700 border-rose-100",
  Settings: "bg-stone-50 text-stone-700 border-stone-200"
};

export default function FeatureModuleTable() {
  const { lang } = useHomeLanguage();
  const sectionRef = useRef<HTMLElement>(null);
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.08 }
    );
    sectionRef.current?.querySelectorAll(".animate-on-scroll").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const filteredRows = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return featureModuleRows;
    return featureModuleRows.filter((row) =>
    [row.category, row.module, row.summary].some((value) => value.toLowerCase().includes(term))
    );
  }, [query]);

  useEffect(() => {
    setCurrentPage(1);
  }, [query]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const startIdx = (safePage - 1) * ITEMS_PER_PAGE;
  const endIdx = startIdx + ITEMS_PER_PAGE;
  const paginatedRows = filteredRows.slice(startIdx, endIdx);
  const pageButtonsMobile = useMemo(() => {
    const max = 4;
    let start = Math.max(1, safePage - Math.floor(max / 2));
    let end = start + max - 1;
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - max + 1);
    }
    return Array.from({ length: Math.max(0, end - start + 1) }, (_, i) => start + i);
  }, [safePage, totalPages]);
  const pageButtonsDesktop = useMemo(() => {
    const max = 8;
    let start = Math.max(1, safePage - Math.floor(max / 2));
    let end = start + max - 1;
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - max + 1);
    }
    return Array.from({ length: Math.max(0, end - start + 1) }, (_, i) => start + i);
  }, [safePage, totalPages]);

  return (
    <section id="fitur" className="py-24 lg:py-28 bg-white relative overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 animate-on-scroll">
          <span className="section-label mb-5 inline-flex">
            <span className="w-1.5 h-1.5 rounded-full bg-primary pulse-dot inline-block" />
            {lang === "id" ? "Layanan dan Fitur" : "Services & Features"}
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-[3rem] font-bold text-fg leading-[1.15] tracking-[-0.03em] mb-4 mt-4">
            {lang === "id" ? "Dilengkapi " : "Powered by "}
            <span className="gradient-text">{lang === "id" ? "Ratusan Modul" : "Hundreds of Modules"}</span>
          </h2>
          <p className="text-fg-muted text-[18px] max-w-3xl mx-auto leading-relaxed">
            {lang === "id"
              ? "Mahal pakai banyak aplikasi untuk urus perusahaan? HRM, Accounting, CRM, Project Management, penyimpanan, dan banyak lagi semua ada di Kolabo cukup bayar 500rb/bulan."
              : "Tired of paying for too many apps? HRM, Accounting, CRM, Project Management, storage, and more are all available in Kolabo for only IDR 500k/month."}
          </p>
        </div>

        <div className="animate-on-scroll rounded-2xl border border-stone-200 bg-white shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-stone-200 flex flex-col lg:flex-row lg:items-center gap-3 lg:justify-between">
            <div className="text-sm text-fg-muted">
              {lang === "id" ? "Menampilkan " : "Showing "}
              <span className="font-semibold text-fg">{filteredRows.length === 0 ? 0 : startIdx + 1}</span> -{" "}
              <span className="font-semibold text-fg">{Math.min(endIdx, filteredRows.length)}</span>
              {lang === "id" ? " dari " : " of "}
              <span className="font-semibold text-fg">{filteredRows.length}</span>
              {lang === "id" ? " hasil" : " results"}
              <span className="text-fg-muted/70">
                {" "}
                {lang === "id"
                  ? `(total ${featureModuleRows.length} modul/fitur)`
                  : `(total ${featureModuleRows.length} modules/features)`}
              </span>
              .
            </div>
            <div className="relative w-full lg:w-[420px]">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={lang === "id" ? "Cari modul, fitur, atau kata kunci..." : "Search modules, features, or keywords..."}
                className="w-full rounded-lg border border-stone-200 bg-white px-4 py-2.5 text-sm text-fg placeholder:text-fg-muted/60 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all"
              />
            </div>
          </div>

          <div className="md:hidden">
            {paginatedRows.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-fg-muted">
                {lang === "id" ? "Tidak ada data yang sesuai pencarian." : "No data matches your search."}
              </div>
            ) : (
              <div className="w-full overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch]">
                <table className="w-full min-w-[860px]">
                  <thead className="bg-stone-50">
                    <tr className="text-left">
                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-[0.12em] text-fg-muted">{lang === "id" ? "Kategori" : "Category"}</th>
                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-[0.12em] text-fg-muted">{lang === "id" ? "Modul" : "Module"}</th>
                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-[0.12em] text-fg-muted">{lang === "id" ? "Ringkasan" : "Summary"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRows.map((row, i) => (
                      <tr key={`${row.category}-${row.module}-${i}`} className="border-t border-stone-100 align-top">
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-md border px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${categoryStyle[row.category] || "bg-stone-50 text-stone-700 border-stone-200"}`}
                          >
                            {row.category}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-fg min-w-[230px]">{row.module}</td>
                        <td className="px-4 py-3 text-sm text-fg-muted leading-relaxed min-w-[430px]">{row.summary}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="hidden md:block overflow-auto max-h-[640px]">
            <table className="w-full min-w-[860px]">
              <thead className="bg-stone-50 sticky top-0 z-10">
                <tr className="text-left">
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-fg-muted">{lang === "id" ? "Kategori" : "Category"}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-fg-muted">{lang === "id" ? "Modul" : "Module"}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-fg-muted">{lang === "id" ? "Ringkasan" : "Summary"}</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRows.map((row, i) =>
                <tr key={`${row.category}-${row.module}-${i}`} className="border-t border-stone-100 align-top">
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-md border px-2.5 py-1 text-xs font-semibold ${categoryStyle[row.category] || "bg-stone-50 text-stone-700 border-stone-200"}`}>
                        {row.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-fg">{row.module}</td>
                    <td className="px-4 py-3 text-sm text-fg-muted leading-relaxed">{row.summary}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="relative z-20 px-4 sm:px-5 py-4 border-t border-stone-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-fg-muted">
              {lang === "id" ? "Halaman " : "Page "}
              <span className="font-semibold text-fg">{safePage}</span>
              {lang === "id" ? " dari " : " of "}
              <span className="font-semibold text-fg">{totalPages}</span>
            </p>
            <div className="w-full sm:w-auto">
              <div className="md:hidden w-full overflow-x-auto pb-1 whitespace-nowrap [-webkit-overflow-scrolling:touch]">
                <div className="inline-flex items-center gap-2 min-w-max px-0.5">
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={safePage === 1}
                  className="shrink-0 px-2.5 py-1.5 text-sm rounded-md border border-stone-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/35 hover:text-primary transition-all touch-manipulation"
                  aria-label={lang === "id" ? "Halaman sebelumnya" : "Previous page"}
                >
                  {lang === "id" ? "‹" : "‹"}
                </button>
                {pageButtonsMobile.map((page) => (
                  <button
                    key={`m-${page}`}
                    type="button"
                    onClick={() => setCurrentPage(page)}
                    className={`shrink-0 w-9 h-9 text-sm rounded-md border transition-all touch-manipulation ${
                      page === safePage
                        ? "border-primary bg-primary text-white"
                        : "border-stone-200 bg-white text-fg hover:border-primary/35 hover:text-primary"
                    }`}
                  >
                    {page}
                  </button>
                ))}
                {totalPages > pageButtonsMobile[pageButtonsMobile.length - 1] && (
                  <span className="shrink-0 px-1 text-sm text-fg-muted">...</span>
                )}
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={safePage === totalPages}
                  className="shrink-0 px-2.5 py-1.5 text-sm rounded-md border border-stone-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/35 hover:text-primary transition-all touch-manipulation"
                  aria-label={lang === "id" ? "Halaman berikutnya" : "Next page"}
                >
                  {lang === "id" ? "›" : "›"}
                </button>
                </div>
              </div>
              <div className="hidden md:flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={safePage === 1}
                  className="px-3 py-1.5 text-sm rounded-md border border-stone-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/35 hover:text-primary transition-all">
                  {lang === "id" ? "Sebelumnya" : "Prev"}
                </button>
                {pageButtonsDesktop[0] > 1 && <span className="px-1 text-sm text-fg-muted">...</span>}
                {pageButtonsDesktop.map((page) => (
                  <button
                    key={`d-${page}`}
                    type="button"
                    onClick={() => setCurrentPage(page)}
                    className={`w-9 h-9 text-sm rounded-md border transition-all ${
                      page === safePage
                        ? "border-primary bg-primary text-white"
                        : "border-stone-200 bg-white text-fg hover:border-primary/35 hover:text-primary"
                    }`}
                  >
                    {page}
                  </button>
                ))}
                {totalPages > pageButtonsDesktop[pageButtonsDesktop.length - 1] && (
                  <span className="px-1 text-sm text-fg-muted">...</span>
                )}
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={safePage === totalPages}
                  className="px-3 py-1.5 text-sm rounded-md border border-stone-200 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary/35 hover:text-primary transition-all">
                  {lang === "id" ? "Berikutnya" : "Next"}
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>);
}
